import { logger } from '@core/logger';
import { getOrchestratorRootUrl } from '../orchestratorBaseUrl';
/**
 * Booking Service
 * 
 * Servicio para comunicarse con el backend de reservas.
 */

export interface TenantByHost {
  tenantId: string;
  name: string;
}

export interface UIIntent {
  type: string;
  data: any;
  actions?: Array<{
    id: string;
    label: string;
    type: string;
    payload?: { action?: string; bookingId?: string; [k: string]: unknown };
    style?: string;
  }>;
}

export interface BookingResponse {
  sessionId: string;
  uiIntent: UIIntent;
  status?: string;
  appInstance?: any;
  sessionUpdates?: any;
}

export class BookingApiError extends Error {
  status?: number;
  code?: string;
  details?: any;

  constructor(message: string, opts?: { status?: number; code?: string; details?: any }) {
    super(message);
    this.name = 'BookingApiError';
    this.status = opts?.status;
    this.code = opts?.code;
    this.details = opts?.details;
  }
}

export class BookingService {
  /**
   * Resuelve tenant por host (subdominio) para booking por subdominio.
   * Llama al orchestrator GET /web/booking/tenant?host=...
   */
  static async resolveTenantByHost(host: string): Promise<TenantByHost | null> {
    if (!host?.trim()) return null;
    try {
      const url = `${getOrchestratorRootUrl()}/api/v1/web/booking/tenant?host=${encodeURIComponent(host.trim())}`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      const data = await res.json();
      if (data?.tenantId && data?.name != null) {
        return { tenantId: data.tenantId, name: data.name };
      }
      return null;
    } catch (err) {
      logger.warn('resolveTenantByHost failed:', err);
      return null;
    }
  }

  private static async makeRequest<T>(url: string, options?: RequestInit): Promise<T> {
    try {
      const timeoutMs = 30000;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      const method = (options?.method || 'GET').toUpperCase();
      const hasBody = options?.body != null && method !== 'GET' && method !== 'HEAD';
      // Evitar Content-Type en GET para no disparar preflight CORS innecesario.
      const requestOptions: RequestInit = {
        ...options,
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          ...options?.headers, // Headers del usuario primero
          ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        },
      };

      // ✅ Logging para debug (solo en desarrollo)
      if (import.meta.env.DEV) {
        logger.debug('📤 BookingService request:', {
          url,
          method: requestOptions.method || 'GET',
          headers: requestOptions.headers,
          body: requestOptions.body,
          bodyType: typeof requestOptions.body,
        });
      }

      const response = await fetch(url, requestOptions);
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        let parsed: any = null;
        try {
          parsed = errorText ? JSON.parse(errorText) : null;
        } catch {
          parsed = null;
        }
        const message =
          parsed?.message && typeof parsed.message === 'string'
            ? parsed.message
            : `HTTP error! status: ${response.status}, body: ${errorText}`;
        const code =
          typeof parsed?.code === 'string'
            ? parsed.code
            : typeof parsed?.errorCode === 'string'
              ? parsed.errorCode
              : undefined;
        logger.error('❌ BookingService error:', {
          status: response.status,
          statusText: response.statusText,
          error: parsed ?? errorText,
        });
        throw new BookingApiError(message, {
          status: response.status,
          code,
          details: parsed,
        });
      }

      return await response.json();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new Error('Timeout conectando con el servicio de reservas');
      }
      logger.error('Booking service error:', error);
      throw error;
    }
  }

  /**
   * Iniciar flujo de reservas.
   * userId: si está logueado (Clerk), evita crear end_user por sesión.
   * guestId: invitado persistente cuando no está autenticado (misma identidad entre sesiones).
   */
  static async startBooking(
    tenantId: string,
    sessionId?: string,
    language?: string,
    userId?: string,
    guestId?: string,
    telegramInitData?: string,
    botId?: string,
    whatsappWebBookingToken?: string,
    catalogScope?: 'booking' | 'restaurant',
  ): Promise<BookingResponse> {
    const params = new URLSearchParams({ tenant_id: tenantId });
    if (sessionId) {
      params.append('session_id', sessionId);
    }
    if (language) {
      params.append('language', language);
    }
    if (userId) {
      params.append('user_id', userId);
    }
    if (guestId) {
      params.append('guest_id', guestId);
    }
    if (botId?.trim()) {
      params.append('bot_id', botId.trim());
    }
    if (catalogScope) {
      params.append('catalog_scope', catalogScope);
    }

    const headers: Record<string, string> = {};
    if (telegramInitData?.trim()) {
      headers['X-Telegram-Init-Data'] = telegramInitData.trim();
    }
    if (whatsappWebBookingToken?.trim()) {
      headers['X-WhatsApp-Web-Booking-Token'] = whatsappWebBookingToken.trim();
    }

    return this.makeRequest<BookingResponse>(
      `${getOrchestratorRootUrl()}/api/v1/web/booking/start?${params.toString()}`,
      Object.keys(headers).length ? { headers } : undefined,
    );
  }

  /**
   * Procesar acción del flujo.
   * guestId: opcional; invitado persistente (misma identidad entre sesiones).
   */
  static async handleAction(
    tenantId: string,
    sessionId: string,
    action: string,
    payload: any,
    authToken?: string,
    userId?: string,
    language?: string,
    guestId?: string,
    telegramInitData?: string,
    botId?: string,
    whatsappWebBookingToken?: string,
    catalogScope?: 'booking' | 'restaurant',
  ): Promise<BookingResponse> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    if (telegramInitData?.trim()) {
      headers['X-Telegram-Init-Data'] = telegramInitData.trim();
    }
    if (whatsappWebBookingToken?.trim()) {
      headers['X-WhatsApp-Web-Booking-Token'] = whatsappWebBookingToken.trim();
    }

    const bodyPayload = { ...payload };
    if (language) bodyPayload.language = language;
    if (catalogScope) bodyPayload.catalog_scope = catalogScope;
    const body: Record<string, unknown> = {
      tenant_id: tenantId,
      session_id: sessionId,
      action,
      payload: bodyPayload,
    };
    if (userId) body.userId = userId;
    if (authToken) body.authToken = authToken;
    if (guestId) body.guestId = guestId;
    if (telegramInitData?.trim()) body.telegramInitData = telegramInitData.trim();
    if (botId?.trim()) body.bot_id = botId.trim();
    if (whatsappWebBookingToken?.trim()) {
      body.whatsapp_web_booking_token = whatsappWebBookingToken.trim();
    }

    return this.makeRequest<BookingResponse>(`${getOrchestratorRootUrl()}/api/v1/web/booking/action`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
  }

  /**
   * Vincular sesión de invitado al usuario Clerk (merge) tras login.
   * POST /web/booking/merge con Authorization Bearer.
   */
  static async mergeGuestWithUser(
    tenantId: string,
    guestId: string,
    authToken: string,
    userId: string,
  ): Promise<{ merged: boolean; endUserId: string }> {
    return this.makeRequest<{ merged: boolean; endUserId: string }>(
      `${getOrchestratorRootUrl()}/api/v1/web/booking/merge`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ tenantId, guestId, userId }),
      },
    );
  }
}
